import { SchemaProps } from '@evertel/types'

/**
 * Types for the DnD Schema Builder v2
 *
 * These types bridge the dnd-kit drag data with the legacy schema structure.
 * The actual schema format (layouts.full, fields, internalSchemas) remains unchanged.
 */

// Drag data attached to draggable items
export interface DragData {
    type: 'layout' | 'element'
    layoutType?: 'row' | 'spacer' | 'list'
    elementType?: string
    fromToolbox: boolean
    rowIndex?: number      // For existing rows/elements: which row
    elementIndex?: number  // For existing elements: position in row
}

// Parsed schema row for internal use during rendering
// This is derived from the schema.layouts.full array
// All rows are type: 'row' - the item type determines rendering behavior
export interface ParsedRow {
    index: number
    type: 'row'
    items: ParsedItem[]
}

// Parsed item within a row - flattened structure matching normalized schema
// Contains id, type, and all other props from the field/item
export interface ParsedItem {
    index: number
    id: string
    type: string
    // All other field props are spread here (title, description, height, internalSchemaId, items, etc.)
    [key: string]: unknown
}

// Position tracking for placeholder
export interface PlaceholderPosition {
    rowIndex: number
    elementIndex: number
}

/**
 * Helper to check if a row contains a spacer or list (single special item)
 */
export const isSpecialRow = (row: ParsedRow): boolean => {
    if (row.items.length !== 1) return false
    const itemType = row.items[0].type
    return itemType === 'spacer' || itemType === 'list'
}

/**
 * Helper to check if a row can accept element drops
 */
export const canRowAcceptElements = (row: ParsedRow): boolean => {
    return !isSpecialRow(row)
}

/**
 * Utility functions to parse legacy schema structure into our internal format
 */

/**
 * Parse a schema row item to determine its type and extract relevant data
 * @param row - The raw row from schema.layouts.full
 * @param index - The row index
 * @param schema - The current schema (for field lookups)
 * @param rootSchema - The root schema (for internalSchemas lookups, needed for recursive list parsing)
 */
export const parseSchemaRow = (
    row: Record<string, unknown>,
    index: number,
    schema: SchemaProps,
    rootSchema: SchemaProps
): ParsedRow => {
    const fields = schema.fields as Record<string, Record<string, unknown>> || {}
    const internalSchemas = rootSchema.internalSchemas as Record<string, SchemaProps> || {}

    // Check if this is a legacy spacer row (type: 'spacer' at row level)
    if (row.type === 'spacer') {
        // Legacy spacer at row level - no field entry exists
        // Create a synthetic item for it
        return {
            index,
            type: 'row',
            items: [{
                index: 0,
                id: `legacy-spacer-${index}`,  // Synthetic ID for legacy spacers
                type: 'spacer',
                height: (row.height as number) || 30,
                border: (row.border as boolean) || false,
                isLegacy: true  // Flag to identify legacy spacers that need conversion on edit
            }]
        }
    }

    const items = row.items as Array<Record<string, unknown>> | undefined

    if (!items || items.length === 0) {
        return {
            index,
            type: 'row',
            items: []
        }
    }

    // Parse each item in the row
    const parsedItems: ParsedItem[] = items.map((item, idx) => {
        const itemType = item.type as string

        // Field reference: type starts with '#'
        if (itemType?.startsWith('#')) {
            const fieldId = itemType.substring(1)
            const fieldData = fields[fieldId] || {}
            return {
                index: idx,
                id: fieldId,
                type: fieldData.type as string,
                ...fieldData
            }
        }

        // List/subform reference: type starts with '&'
        if (itemType?.startsWith('&')) {
            const listId = itemType.substring(1)
            const internalSchema = internalSchemas[listId]

            // Recursively parse the internal schema's rows
            const listItems = internalSchema
                ? parseSchemaRows(internalSchema, rootSchema)
                : []

            return {
                index: idx,
                id: listId,
                type: 'list',
                internalSchemaId: listId,
                title: (item.title as string) || 'Sub Form',
                description: (item.description as string) || '',
                hint: (item.hint as string) || '',
                items: listItems
            }
        }

        // Direct type (legacy support) - item already has type and props inline
        return {
            index: idx,
            id: (item.id as string) || `unknown-${idx}`,
            type: itemType,
            ...item
        }
    })

    return {
        index,
        type: 'row',
        items: parsedItems
    }
}

/**
 * Parse the entire schema layouts.full array into ParsedRow[]
 * @param schema - The schema to parse (could be root or internal schema)
 * @param rootSchema - The root schema (optional, defaults to schema - used for internalSchemas access)
 */
export const parseSchemaRows = (schema: SchemaProps, rootSchema?: SchemaProps): ParsedRow[] => {
    const layouts = schema.layouts as Record<string, unknown> | undefined
    if (!layouts) return []

    const fullLayout = layouts.full as Array<Record<string, unknown>> | undefined
    if (!fullLayout) return []

    // Use rootSchema if provided, otherwise use schema (for root-level calls)
    const root = rootSchema || schema

    return fullLayout.map((row, index) => parseSchemaRow(row, index, schema, root))
}
